import React from "react";
import PropTypes from "prop-types";
import "../styles/CurriculumSection.css";

/**
 * CurriculumSection
 *
 * Figma-aligned section with centered header + two info cards.
 * Left card: dark (#243A24) with white text.
 * Right card: light with outline + CTA button.
 *
 * Props are optional; sensible defaults mirror your Figma content.
 */
const CurriculumSection = ({
  eyebrow = "CURRICULUM",
  title = "Flexible learning for real futures.",
  intro = "We offer the Victorian Pathways Certificate (VPC), a hands-on, inclusive senior secondary qualification. Students complete tailored learning plans with subjects in literacy, numeracy, work skills, and personal development—preparing them for further study, training, or employment.",
  leftTitle = "The Victorian Pathways Certificate (VPC)",
  leftPoints = [
    "Inclusive and flexible option for completing Secondary Education.",
    "For students not yet ready for VCE or VCE VM.",
    "Can begin earlier than Year 11 or extend beyond two years.",
    "Focuses on practical, classroom-based assessment.",
    "Designed collaboratively with students, families, and staff.",
  ],
  rightTitle = "Pathways from the VPC",
  rightPoints = [
    "Transition to VCE or VCE VM.",
    "Entry-level VET programs at TAFE.",
    "Apprenticeships and traineeships.",
    "Direct entry into the workforce.",
  ],
  cta = { label: "Learn More", href: "/curriculum" },
}) => {
  return (
    <section className="curr-section">
      {/* Header */}
      <div className="curr-header">
        <p className="curr-eyebrow" aria-label={eyebrow}>{eyebrow}</p>
        <h2 className="curr-title">{title}</h2>
        <p className="curr-intro">{intro}</p>
      </div>

      {/* Cards row */}
      <div className="curr-row">
        {/* Left card (dark) */}
        <article className="curr-card curr-card--dark" aria-labelledby="curr-left-title">
          <h3 id="curr-left-title" className="curr-card-title curr-card-title--light">
            {leftTitle}
          </h3>
          <ul className="curr-list curr-list--light" role="list">
            {leftPoints.map((p, i) => (
              <li key={i} className="curr-list-item">{p}</li>
            ))}
          </ul>
        </article>

        {/* Right card (light) */}
        <article className="curr-card curr-card--light" aria-labelledby="curr-right-title">
          <h3 id="curr-right-title" className="curr-card-title">{rightTitle}</h3>
          <ul className="curr-list" role="list">
            {rightPoints.map((p, i) => (
              <li key={i} className="curr-list-item curr-list-item--muted">{p}</li>
            ))}
          </ul>

          {cta?.label && cta?.href && (
            <a className="curr-cta" href={cta.href} aria-label={cta.label}>
              {cta.label}
            </a>
          )}
        </article>
      </div>
    </section>
  );
};

CurriculumSection.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  intro: PropTypes.string,
  leftTitle: PropTypes.string,
  leftPoints: PropTypes.arrayOf(PropTypes.string),
  rightTitle: PropTypes.string,
  rightPoints: PropTypes.arrayOf(PropTypes.string),
  cta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  }),
};

export default CurriculumSection;
